import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { Chart, LinearScale, LineElement, PointElement, ScatterController, Tooltip } from "chart.js";
import type { ChartDataset, ChartOptions, Plugin } from "chart.js";

import {
  Box,
  IconButton,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Tooltip as MuiTooltip,
  useTheme,
} from "@mui/material";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";

import { SelectionZoomPlugin } from "../../../../../../components/common/chart-plugins/selection-zoom";
import type {
  IChartZoomPayload,
  IAxisZoomBounds,
  ISelectionZoomPluginOptions,
} from "../../../../../../components/common/chart-plugins/selection-zoom";
import { CHART_SELECTION_EVENTS, useChartZoom } from "../../../scheme/components/analytics/components/chart-zoom";
import { ChartZoomOverview } from "../../../scheme/components/analytics/components/chart-zoom-overview";
import { ROTATION_DIRECTION } from "../../../multi-analysis/types";
import {
  buildNyquistPoints,
  formatNyquistWallClockTick,
  getNyquistArrowShape,
  getNyquistVisibleArrowSegments,
  getNyquistKeyboardCursor,
  getNyquistPolarBounds,
  getNyquistPolarExtent,
  getNyquistRotationFrequencyPoints,
  getNyquistRotationIndicator,
  getNyquistTimeTicks,
  orderNyquistSeries,
  parseNyquistWallClock,
  layoutNyquistRotationFrequencyLabels,
} from "./chart-utils";
import type {
  INyquistChartPoint,
  INyquistChartSeries,
  INyquistLabelBounds,
  INyquistPlacedRotationFrequencyLabel,
  NyquistChartKind,
} from "./chart-utils";
import { formatNyquistDate } from "./model";
import { fitNyquistPolarViewport, getNyquistPolarZoomPayload } from "./zoom-utils";
import { getNyquistTooltipLines, getNyquistTooltipPosition, NYQUIST_TOOLTIP_COLORS } from "./tooltip-utils";

export type { INyquistChartSample, INyquistChartSeries } from "./chart-utils";

export interface INyquistChartProps {
  kind: NyquistChartKind;
  series: readonly INyquistChartSeries[];
  cursorKey: string | null;
  onCursorChange: (key: string) => void;
  formatValue: (value: number, unitCode: number | null) => string;
  rotationDirection?: ROTATION_DIRECTION;
  showRotationFrequency?: boolean;
  hiddenSeriesKeys?: readonly string[];
}

Chart.register(ScatterController, LinearScale, LineElement, PointElement, Tooltip, SelectionZoomPlugin);

type NyquistChart = Chart<"scatter", INyquistChartPoint[]>;
type NyquistDataset = ChartDataset<"scatter", INyquistChartPoint[]>;

const isSelectionActive = (chart: Chart) => {
  const state = (
    chart as Chart & {
      selectionZoom?: { mouseDown?: boolean; dragging?: boolean; suppressClickLock?: number };
    }
  ).selectionZoom;
  return Boolean(state?.mouseDown || state?.dragging || state?.suppressClickLock);
};

interface INyquistSelectionMenu {
  source: readonly INyquistChartSeries[];
  kind: NyquistChartKind;
  position: { top: number; left: number };
  points: INyquistChartPoint[];
}

const formatSampleDate = (date: string) => {
  const fraction = /\.(\d+)/.exec(date)?.[1];
  return `${formatNyquistDate(date)}${fraction ? `.${fraction}` : ""}`;
};

const formatPointValues = (
  point: INyquistChartPoint,
  source: INyquistChartSeries,
  formatValue: INyquistChartProps["formatValue"],
) => {
  const phase = point.sample.phaseDegrees;
  return `${formatValue(point.sample.amplitude, source.unitCode)} ${source.units} / ${
    phase !== null && Number.isFinite(phase) ? `${formatValue(phase, 133)}°` : "—"
  }`;
};

const drawArrow = (
  context: CanvasRenderingContext2D,
  from: { x: number; y: number },
  to: { x: number; y: number },
  bounds: INyquistLabelBounds,
) => {
  const shape = getNyquistArrowShape(from, to);
  if (!shape) return;
  if (
    shape.tip.x < bounds.left ||
    shape.tip.x > bounds.right ||
    shape.tip.y < bounds.top ||
    shape.tip.y > bounds.bottom
  )
    return;
  context.save();
  context.beginPath();
  context.rect(bounds.left, bounds.top, bounds.right - bounds.left, bounds.bottom - bounds.top);
  context.clip();
  context.beginPath();
  context.moveTo(shape.tip.x, shape.tip.y);
  context.lineTo(shape.left.x, shape.left.y);
  context.moveTo(shape.tip.x, shape.tip.y);
  context.lineTo(shape.right.x, shape.right.y);
  context.strokeStyle = "#000";
  context.lineWidth = 2;
  context.lineCap = "round";
  context.stroke();
  context.restore();
};

export const NyquistChart = ({
  kind,
  series,
  cursorKey,
  onCursorChange,
  formatValue,
  rotationDirection = ROTATION_DIRECTION.UNDEFINED,
  showRotationFrequency = false,
  hiddenSeriesKeys,
}: INyquistChartProps) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const hintId = useId();
  const frequencyDescriptionId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const tooltipHoveredRef = useRef(false);
  const tooltipFocusedRef = useRef(false);
  const chartRef = useRef<NyquistChart | null>(null);
  const cursorRef = useRef(cursorKey);
  const onCursorChangeRef = useRef(onCursorChange);
  const rotationRef = useRef(rotationDirection);
  const rotationFrequencyRef = useRef(showRotationFrequency);
  const suppressNextClickRef = useRef(false);
  const polarDomainRef = useRef<{ x: IAxisZoomBounds; y: IAxisZoomBounds } | null>(null);
  const [plotSize, setPlotSize] = useState({ width: 0, height: 0 });
  const [selectionMenu, setSelectionMenu] = useState<INyquistSelectionMenu | null>(null);
  cursorRef.current = cursorKey;
  onCursorChangeRef.current = onCursorChange;
  rotationRef.current = rotationDirection;
  rotationFrequencyRef.current = showRotationFrequency;
  const orderedSeries = useMemo(() => orderNyquistSeries(series), [series]);
  const {
    zoomState,
    applySelectionZoom,
    clearZoomState: clearChartZoomState,
    resetZoom: resetChartZoom,
    isZoomed,
    overview,
    panZoom,
    overviewPlugin,
    refreshOverview,
  } = useChartZoom(chartRef, {}, orderedSeries);
  const clearZoomState = useCallback(() => {
    polarDomainRef.current = null;
    clearChartZoomState();
  }, [clearChartZoomState]);
  const resetZoom = useCallback(() => {
    polarDomainRef.current = null;
    resetChartZoom();
  }, [resetChartZoom]);
  const applyZoom = useCallback(
    (payload: IChartZoomPayload) => {
      const chart = chartRef.current;
      suppressNextClickRef.current = true;
      if (kind !== "polar" || !chart) {
        applySelectionZoom(payload);
        return;
      }
      const normalized = getNyquistPolarZoomPayload(payload, { x: chart.scales.x, y: chart.scales.y });
      polarDomainRef.current ??= {
        x: { min: chart.scales.x.min, max: chart.scales.x.max },
        y: { min: chart.scales.y.min, max: chart.scales.y.max },
      };
      const bounds = fitNyquistPolarViewport(
        { x: normalized.x ?? chart.scales.x, y: normalized.y?.y ?? chart.scales.y },
        chart.chartArea.width,
        chart.chartArea.height,
        polarDomainRef.current,
      );
      applySelectionZoom({ axisMode: "xy", x: bounds.x, y: { y: bounds.y } });
    },
    [applySelectionZoom, kind],
  );
  const effectiveZoomState = useMemo(() => {
    if (kind !== "polar" || !zoomState?.x || !zoomState.y?.y || !plotSize.width || !plotSize.height) return zoomState;
    const bounds = fitNyquistPolarViewport(
      { x: zoomState.x, y: zoomState.y.y },
      plotSize.width,
      plotSize.height,
      polarDomainRef.current ?? undefined,
    );
    return { ...zoomState, x: bounds.x, y: { y: bounds.y } };
  }, [kind, plotSize, zoomState]);
  const hiddenSeriesRef = useRef(hiddenSeriesKeys);
  hiddenSeriesRef.current = hiddenSeriesKeys;
  const keyboardSeries = useMemo(
    () => orderedSeries.filter(item => !hiddenSeriesKeys?.includes(item.key)),
    [hiddenSeriesKeys, orderedSeries],
  );
  const chartLabel = formatMessage({ id: `pages.speed.nyquist.${kind === "polar" ? "polarChart" : "cartesianChart"}` });
  const keyboardHint = formatMessage({ id: "pages.speed.nyquist.chartKeyboardHint" });
  const coincidentTitle = formatMessage({ id: "pages.speed.nyquist.coincidentMeasurements" });
  const selectionLabel = formatMessage({ id: "pages.speed.nyquist.selectMeasurement" });
  const frequencyUnit = formatMessage({ id: "pages.scheme.analytics.rotationFrequencyRpmUnit" });
  const visibleMenu = selectionMenu?.source === series && selectionMenu.kind === kind ? selectionMenu : null;
  const sourceByKey = useMemo(() => new Map(series.map(item => [item.key, item])), [series]);
  const samplesByKey = useMemo(
    () => new Map(orderedSeries.flatMap(item => item.samples.map(sample => [sample.key, sample] as const))),
    [orderedSeries],
  );
  const selectedSample = cursorKey === null ? undefined : samplesByKey.get(cursorKey);
  const formatFrequency = (rpm: number | null) =>
    `${rpm !== null && Number.isFinite(rpm) ? formatValue(rpm, 140) : "—"} ${frequencyUnit}`;
  const frequencyDescription =
    kind === "polar" && showRotationFrequency && selectedSample
      ? `${formatSampleDate(selectedSample.date)} · ${formatFrequency(selectedSample.rotationFrequencyRpm)}`
      : "";

  useEffect(() => {
    setSelectionMenu(null);
  }, [kind, series]);

  useEffect(() => {
    resetZoom();
  }, [kind, orderedSeries, resetZoom]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const textColor = theme.palette.text.secondary;
    const gridColor = theme.palette.divider;
    const font = { size: 12, family: theme.typography.fontFamily };
    const amplitudeTitle = formatMessage({ id: "pages.speed.nyquist.amplitude" });
    const phaseTitle = formatMessage({ id: "pages.speed.nyquist.phase" });
    const amplitudeUnits = Array.from(new Map(orderedSeries.map(item => [item.unitCode, item.units])));
    const datesByTime = new Map<number, string>();
    orderedSeries.forEach(item =>
      item.samples.forEach(sample => datesByTime.set(parseNyquistWallClock(sample.date)!, sample.date)),
    );
    const sampleTimes = Array.from(datesByTime.keys());
    const start = sampleTimes.length ? sampleTimes.reduce((minimum, value) => Math.min(minimum, value)) : 0;
    const end = sampleTimes.length ? sampleTimes.reduce((maximum, value) => Math.max(maximum, value)) : 1;
    const extent = getNyquistPolarExtent(orderedSeries);
    const data: NyquistDataset[] = orderedSeries.flatMap(item => {
      const amplitude: NyquistDataset = {
        label: item.name,
        data: buildNyquistPoints(item, kind),
        yAxisID: kind === "polar" ? "y" : `amplitude-${item.unitCode}`,
        borderColor: item.color,
        backgroundColor: item.color,
        borderWidth: 1.8,
        pointRadius: 2.5,
        pointHoverRadius: 5,
        pointHitRadius: 8,
        showLine: true,
        spanGaps: false,
        tension: 0,
        hidden: hiddenSeriesRef.current?.includes(item.key) ?? false,
      };
      return kind === "polar"
        ? [amplitude]
        : [
            amplitude,
            {
              ...amplitude,
              label: `${item.name} · ${phaseTitle}`,
              data: buildNyquistPoints(item, kind, true),
              yAxisID: "phase",
              borderDash: [5, 4],
              borderWidth: 1.4,
            },
          ];
    });
    const pointIndexes = data.map(dataset => new Map(dataset.data.map((point, index) => [point.key, index])));
    const frequencyPoints =
      kind === "polar"
        ? data.flatMap((dataset, datasetIndex) =>
            getNyquistRotationFrequencyPoints(dataset.data).map(point => ({
              point,
              datasetIndex,
              pointIndex: pointIndexes[datasetIndex].get(point.key)!,
            })),
          )
        : [];
    const frequencyPointsByKey = new Map(frequencyPoints.map(point => [point.point.key, point]));
    const states = new WeakMap<
      Chart,
      {
        frequencyLayout: INyquistPlacedRotationFrequencyLabel[] | null;
        frequencyRotation: ROTATION_DIRECTION;
        polarObstacles: INyquistLabelBounds[];
      }
    >();
    const getState = (chart: Chart) => {
      let state = states.get(chart);
      if (!state) {
        state = { frequencyLayout: null, frequencyRotation: ROTATION_DIRECTION.UNDEFINED, polarObstacles: [] };
        states.set(chart, state);
      }
      return state;
    };
    const phases = new Map(
      kind === "cartesian"
        ? data
            .filter(dataset => dataset.yAxisID === "phase")
            .flatMap(dataset => dataset.data.map(point => [point.key, point.y] as const))
        : [],
    );
    const tooltipLines = (point: INyquistChartPoint) => {
      const source = sourceByKey.get(point.seriesKey);
      if (!source) return [];
      return getNyquistTooltipLines(
        {
          pointName: source.pointName,
          measureName: source.measureName,
          date: point.sample.date,
          amplitude: point.sample.amplitude,
          phaseDegrees: kind === "cartesian" ? phases.get(point.key) ?? null : point.sample.phaseDegrees,
          unitCode: source.unitCode,
          amplitudeUnits: source.units,
          phaseUnits: "°",
        },
        {
          point: formatMessage({ id: "pages.multiAnalysis.timePanel.tooltip.point" }),
          measure: formatMessage({ id: "pages.multiAnalysis.timePanel.tooltip.measure" }),
          dateTime: formatMessage({ id: "pages.multiAnalysis.timePanel.tooltip.dateTime" }),
          amplitude: amplitudeTitle,
          phase: phaseTitle,
        },
        formatValue,
      );
    };

    const decoration: Plugin<"scatter"> = {
      id: "nyquistDecoration",
      beforeEvent: (chart, { event }) => {
        if (event.type === "click" && suppressNextClickRef.current) {
          suppressNextClickRef.current = false;
          return false;
        }
        const { frequencyLayout } = getState(chart);
        if (isSelectionActive(chart)) return;
        if (
          kind !== "polar" ||
          !rotationFrequencyRef.current ||
          !frequencyLayout ||
          event.x === null ||
          event.y === null ||
          !["mousemove", "click"].includes(event.type)
        )
          return;
        let label: INyquistPlacedRotationFrequencyLabel | undefined;
        for (let index = frequencyLayout.length - 1; index >= 0; index -= 1) {
          const candidate = frequencyLayout[index];
          if (
            event.x >= candidate.left &&
            event.x <= candidate.right &&
            event.y >= candidate.top &&
            event.y <= candidate.bottom
          ) {
            label = candidate;
            break;
          }
        }
        const target = label ? frequencyPointsByKey.get(label.key) : undefined;
        if (!target || !chart.isDatasetVisible(target.datasetIndex)) return;
        const active = [{ datasetIndex: target.datasetIndex, index: target.pointIndex }];
        chart.setActiveElements(active);
        chart.tooltip?.setActiveElements(active, { x: event.x, y: event.y });
        if (event.type !== "mousemove") {
          setSelectionMenu(null);
          onCursorChangeRef.current(target.point.key);
        }
        chart.draw();
        return false;
      },
      afterLayout: chart => {
        if (kind !== "polar") return;
        getState(chart).frequencyLayout = null;
        const bounds = getNyquistPolarBounds(extent, chart.chartArea.width, chart.chartArea.height);
        const x = chart.scales.x;
        const y = chart.scales.y;
        const viewport = fitNyquistPolarViewport(
          {
            x: zoomState?.x ?? { min: -bounds.x, max: bounds.x },
            y: zoomState?.y?.y ?? { min: -bounds.y, max: bounds.y },
          },
          chart.chartArea.width,
          chart.chartArea.height,
          polarDomainRef.current ?? undefined,
        );
        x.min = viewport.x.min;
        x.max = viewport.x.max;
        y.min = viewport.y.min;
        y.max = viewport.y.max;
        x.configure();
        y.configure();
      },
      afterUpdate: chart => {
        getState(chart).frequencyLayout = null;
      },
      beforeDatasetsDraw: chart => {
        if (kind !== "polar") return;
        const context = chart.ctx;
        const x = chart.scales.x;
        const y = chart.scales.y;
        const centerX = x.getPixelForValue(0);
        const centerY = y.getPixelForValue(0);
        const radius = Math.abs(x.getPixelForValue(extent) - centerX);
        const rings = Math.max(1, Math.min(4, Math.floor(radius / 24)));
        const angles = radius < 90 ? 4 : 12;
        context.save();
        context.font = `${font.size}px ${font.family}`;
        context.fillStyle = textColor;
        context.strokeStyle = gridColor;
        context.lineWidth = 1;
        const { polarObstacles } = getState(chart);
        polarObstacles.length = 0;
        for (let ring = 1; ring <= rings; ring += 1) {
          const ringRadius = (radius * ring) / rings;
          context.beginPath();
          context.arc(centerX, centerY, ringRadius, 0, Math.PI * 2);
          context.stroke();
          context.textAlign = "left";
          const text = formatValue((extent * ring) / rings, orderedSeries[0]?.unitCode ?? null);
          const labelY = centerY - ringRadius + 12;
          context.fillText(text, centerX + 5, labelY);
          polarObstacles.push({
            left: centerX + 3,
            top: labelY - font.size,
            right: centerX + 7 + context.measureText(text).width,
            bottom: labelY + 3,
          });
        }
        context.textAlign = "center";
        for (let index = 0; index < angles; index += 1) {
          const degrees = (index * 360) / angles;
          const angle = (degrees * Math.PI) / 180;
          context.beginPath();
          context.moveTo(centerX, centerY);
          context.lineTo(centerX + Math.cos(angle) * radius, centerY - Math.sin(angle) * radius);
          context.stroke();
          const text = `${degrees}°`;
          const labelX = centerX + Math.cos(angle) * (radius + 18);
          const labelY = centerY - Math.sin(angle) * (radius + 18) + 4;
          const labelWidth = context.measureText(text).width;
          context.fillText(text, labelX, labelY);
          polarObstacles.push({
            left: labelX - labelWidth / 2 - 2,
            top: labelY - font.size,
            right: labelX + labelWidth / 2 + 2,
            bottom: labelY + 3,
          });
        }
        context.fillText(orderedSeries[0]?.units ?? "", centerX, chart.height - 5);
        context.restore();
      },
      afterDatasetsDraw: chart => {
        const state = getState(chart);
        const context = chart.ctx;
        context.save();
        if (kind === "polar") {
          const arrowSegments = data.map((dataset, datasetIndex) => {
            const rendered = chart.getDatasetMeta(datasetIndex).data;
            return getNyquistVisibleArrowSegments(
              dataset.data.map((point, index) =>
                Number.isFinite(point.x) && Number.isFinite(point.y) && rendered[index]
                  ? rendered[index]
                  : { x: Number.NaN, y: Number.NaN },
              ),
              chart.chartArea,
            );
          });
          const center = { x: chart.scales.x.getPixelForValue(0), y: chart.scales.y.getPixelForValue(0) };
          const radius = Math.abs(chart.scales.x.getPixelForValue(extent) - center.x);
          const indicator = getNyquistRotationIndicator(center, radius, rotationRef.current, {
            left: 0,
            top: 0,
            right: chart.width,
            bottom: chart.height,
          });
          if (indicator) {
            context.strokeStyle = theme.palette.text.primary;
            context.lineWidth = 2;
            context.lineCap = "round";
            context.beginPath();
            context.arc(
              indicator.x,
              indicator.y,
              indicator.radius,
              indicator.start,
              indicator.end,
              indicator.anticlockwise,
            );
            context.stroke();
            context.beginPath();
            context.moveTo(indicator.head.x, indicator.head.y);
            context.lineTo(
              indicator.head.x - 12 * Math.cos(indicator.headDirection - Math.PI / 8),
              indicator.head.y - 12 * Math.sin(indicator.headDirection - Math.PI / 8),
            );
            context.moveTo(indicator.head.x, indicator.head.y);
            context.lineTo(
              indicator.head.x - 12 * Math.cos(indicator.headDirection + Math.PI / 8),
              indicator.head.y - 12 * Math.sin(indicator.headDirection + Math.PI / 8),
            );
            context.stroke();
          }
          if (rotationFrequencyRef.current) {
            context.font = `${font.size}px ${font.family}`;
            context.textAlign = "left";
            if (state.frequencyRotation !== rotationRef.current) {
              state.frequencyLayout = null;
              state.frequencyRotation = rotationRef.current;
            }
            if (!state.frequencyLayout) {
              const labels = frequencyPoints.flatMap(({ point, datasetIndex, pointIndex }) => {
                if (!chart.isDatasetVisible(datasetIndex)) return [];
                const rendered = chart.getDatasetMeta(datasetIndex).data[pointIndex];
                if (!rendered || !Number.isFinite(rendered.x) || !Number.isFinite(rendered.y)) return [];
                if (
                  rendered.x < chart.chartArea.left ||
                  rendered.x > chart.chartArea.right ||
                  rendered.y < chart.chartArea.top ||
                  rendered.y > chart.chartArea.bottom
                )
                  return [];
                const text = formatValue(point.sample.rotationFrequencyRpm!, 140);
                return [
                  {
                    key: point.key,
                    x: rendered.x,
                    y: rendered.y,
                    text,
                    width: context.measureText(text).width,
                    color: orderedSeries[datasetIndex].color,
                  },
                ];
              });
              const obstacles = [...state.polarObstacles];
              data.forEach((dataset, datasetIndex) => {
                if (!chart.isDatasetVisible(datasetIndex)) return;
                const rendered = chart.getDatasetMeta(datasetIndex).data;
                dataset.data.forEach((point, pointIndex) => {
                  const marker = rendered[pointIndex] as PointElement | undefined;
                  if (
                    !marker ||
                    marker.skip ||
                    !Number.isFinite(point.x) ||
                    !Number.isFinite(point.y) ||
                    !Number.isFinite(marker.x) ||
                    !Number.isFinite(marker.y)
                  )
                    return;
                  obstacles.push({
                    left: marker.x - 7,
                    top: marker.y - 7,
                    right: marker.x + 7,
                    bottom: marker.y + 7,
                  });
                });
                arrowSegments[datasetIndex].forEach(segment => {
                  const shape = getNyquistArrowShape(segment.from, segment.to);
                  if (!shape) return;
                  obstacles.push({
                    left: Math.min(shape.tip.x, shape.left.x, shape.right.x) - 2,
                    top: Math.min(shape.tip.y, shape.left.y, shape.right.y) - 2,
                    right: Math.max(shape.tip.x, shape.left.x, shape.right.x) + 2,
                    bottom: Math.max(shape.tip.y, shape.left.y, shape.right.y) + 2,
                  });
                });
              });
              if (indicator) {
                const startX = indicator.x + Math.cos(indicator.start) * indicator.radius;
                const startY = indicator.y + Math.sin(indicator.start) * indicator.radius;
                obstacles.push({
                  left: Math.min(startX, indicator.head.x) - 14,
                  top: Math.min(startY, indicator.head.y) - 14,
                  right: Math.max(startX, indicator.head.x) + 14,
                  bottom: Math.max(startY, indicator.head.y) + 14,
                });
              }
              state.frequencyLayout = layoutNyquistRotationFrequencyLabels(
                labels,
                { left: 4, top: 4, right: chart.width - 4, bottom: chart.height - 20 },
                obstacles,
                font.size,
              );
            }
            state.frequencyLayout.forEach(label => {
              context.strokeStyle = label.color;
              context.lineWidth = 0.8;
              context.beginPath();
              context.moveTo(label.x, label.y);
              context.lineTo(label.leaderX, label.leaderY);
              context.stroke();
            });
            state.frequencyLayout.forEach(label => {
              context.fillStyle = theme.palette.background.paper;
              context.fillRect(label.left, label.top, label.right - label.left, label.bottom - label.top);
              context.fillStyle = label.color;
              context.fillText(label.text, label.textX, label.textY, Math.max(1, label.right - label.left - 8));
            });
            chart
              .getSortedVisibleDatasetMetas()
              .slice()
              .reverse()
              .forEach(meta => meta.data.forEach(marker => (marker as PointElement).draw(context, chart.chartArea)));
          }
          chart.data.datasets.forEach((_dataset, datasetIndex) => {
            if (!chart.isDatasetVisible(datasetIndex)) return;
            context.fillStyle = orderedSeries[datasetIndex].color;
            arrowSegments[datasetIndex].forEach(segment => {
              drawArrow(context, segment.from, segment.to, chart.chartArea);
            });
          });
        }
        let cursorX: number | null = null;
        data.forEach((dataset, datasetIndex) => {
          if (!chart.isDatasetVisible(datasetIndex)) return;
          const pointIndex = cursorRef.current === null ? undefined : pointIndexes[datasetIndex].get(cursorRef.current);
          if (pointIndex === undefined) return;
          const point = dataset.data[pointIndex];
          const element = chart.getDatasetMeta(datasetIndex).data[pointIndex];
          if (!point || !element || !Number.isFinite(point.x) || !Number.isFinite(point.y)) return;
          if (
            element.x < chart.chartArea.left ||
            element.x > chart.chartArea.right ||
            element.y < chart.chartArea.top ||
            element.y > chart.chartArea.bottom
          )
            return;
          cursorX = element.x;
          context.beginPath();
          context.arc(element.x, element.y, 6, 0, Math.PI * 2);
          context.fillStyle = theme.palette.background.paper;
          context.fill();
          context.strokeStyle = String(dataset.borderColor);
          context.lineWidth = 2;
          context.stroke();
          context.beginPath();
          context.arc(element.x, element.y, 2.5, 0, Math.PI * 2);
          context.fillStyle = String(dataset.borderColor);
          context.fill();
        });
        if (kind === "cartesian" && cursorX !== null) {
          context.beginPath();
          context.setLineDash([3, 4]);
          context.strokeStyle = textColor;
          context.lineWidth = 1;
          context.moveTo(cursorX, chart.chartArea.top);
          context.lineTo(cursorX, chart.chartArea.bottom);
          context.stroke();
        }
        context.restore();
      },
    };

    const disabledPlugins = {
      crosshair: false,
      alarmLevel: false,
      htmlLegend: false,
      drawConnectingLines: false,
      drawOrbitConnectingLines: false,
      harmonicCursors: false,
      subHarmonicCursors: false,
      additionalCursorLabels: false,
      faultFrequenciesCursors: false,
      markerPassageLines: false,
    } as const;
    const selectionZoom: ISelectionZoomPluginOptions = {
      enabled: true,
      captureOutsideCanvas: true,
      minDragPx: 8,
      xScaleId: "x",
      yScaleIds: kind === "polar" ? ["y"] : [...amplitudeUnits.map(([code]) => `amplitude-${code}`), "phase"],
      onZoom: applyZoom,
      onReset: clearZoomState,
    };
    const options: ChartOptions<"scatter"> = {
      animation: false,
      responsive: false,
      maintainAspectRatio: false,
      normalized: false,
      events: CHART_SELECTION_EVENTS,
      layout: { padding: kind === "polar" ? { top: 28, right: 36, bottom: 46, left: 36 } : 0 },
      interaction: { mode: "nearest", intersect: false },
      onClick: (_event, elements, clickedChart) => {
        if (_event.type !== "click") return;
        if (suppressNextClickRef.current) {
          suppressNextClickRef.current = false;
          return;
        }
        if (isSelectionActive(clickedChart)) return;
        const pointsByKey = new Map<string, INyquistChartPoint>();
        elements.forEach(selected => {
          const point = data[selected.datasetIndex]?.data[selected.index];
          if (point && Number.isFinite(point.x) && Number.isFinite(point.y)) pointsByKey.set(point.key, point);
        });
        const points = Array.from(pointsByKey.values());
        if (points.length < 2) {
          setSelectionMenu(null);
          if (points[0]) onCursorChangeRef.current(points[0].key);
          return;
        }
        const nativeEvent = _event.native;
        const bounds = canvas.getBoundingClientRect();
        const left =
          nativeEvent && "clientX" in nativeEvent && typeof nativeEvent.clientX === "number"
            ? nativeEvent.clientX
            : bounds.left + (_event.x ?? 0);
        const top =
          nativeEvent && "clientY" in nativeEvent && typeof nativeEvent.clientY === "number"
            ? nativeEvent.clientY
            : bounds.top + (_event.y ?? 0);
        setSelectionMenu({ source: series, kind, position: { top, left }, points });
      },
      scales:
        kind === "polar"
          ? {
              x: { type: "linear", display: false, min: -extent, max: extent },
              y: { type: "linear", display: false, min: -extent, max: extent },
            }
          : {
              x: {
                type: "linear",
                min: zoomState?.x?.min ?? (start === end ? start - 1000 : start),
                max: zoomState?.x?.max ?? (start === end ? end + 1000 : end),
                title: { display: false },
                grid: { color: gridColor },
                ticks: {
                  color: textColor,
                  font,
                  maxRotation: 0,
                  autoSkip: true,
                  callback: value => {
                    const date = datesByTime.get(Number(value));
                    return formatNyquistWallClockTick(
                      date ?? new Date(Number(value)).toISOString(),
                      end - start >= 86_400_000,
                    );
                  },
                },
                afterBuildTicks: scale => {
                  const ticks = getNyquistTimeTicks(
                    orderedSeries.map(item => ({
                      ...item,
                      samples: item.samples.filter(sample => {
                        const time = parseNyquistWallClock(sample.date)!;
                        return time >= scale.min && time <= scale.max;
                      }),
                    })),
                    Math.max(2, Math.min(6, Math.floor(scale.chart.width / 95))),
                  ).map(tick => ({ value: tick.value }));
                  if (ticks.length >= 2) scale.ticks = ticks;
                },
              },
              ...Object.fromEntries(
                amplitudeUnits.map(([unitCode, units]) => [
                  `amplitude-${unitCode}`,
                  {
                    type: "linear" as const,
                    position: "left" as const,
                    beginAtZero: true,
                    ...zoomState?.y?.[`amplitude-${unitCode}`],
                    grace: "10%",
                    title: { display: true, text: `${amplitudeTitle}, ${units}`, color: textColor, font },
                    ticks: {
                      color: textColor,
                      font,
                      maxTicksLimit: 5,
                      callback: (value: number | string) => formatValue(Number(value), unitCode),
                    },
                    grid: { color: gridColor, drawOnChartArea: unitCode === amplitudeUnits[0]?.[0] },
                  },
                ]),
              ),
              phase: {
                type: "linear",
                position: "right",
                ...zoomState?.y?.phase,
                title: { display: true, text: `${phaseTitle}, °`, color: textColor, font },
                ticks: { color: textColor, font, maxTicksLimit: 5, callback: value => formatValue(Number(value), 133) },
                grid: { drawOnChartArea: false },
                grace: "10%",
              },
            },
      plugins: {
        ...disabledPlugins,
        legend: { display: false },
        ...{ selectionZoom },
        tooltip: {
          enabled: false,
          external: ({ chart: tooltipChart, tooltip }) => {
            const element = tooltipRef.current;
            if (!element) return;
            if (
              isSelectionActive(tooltipChart) ||
              (!tooltip.opacity && !tooltipHoveredRef.current && !tooltipFocusedRef.current)
            ) {
              element.style.display = "none";
              return;
            }
            if (!tooltip.opacity) return;
            const points = [
              ...new Map(
                tooltip.dataPoints.map(item => {
                  const point = data[item.datasetIndex]?.data[item.dataIndex];
                  return [point?.key, point] as const;
                }),
              ).values(),
            ].filter((point): point is INyquistChartPoint => Boolean(point));
            const anchor = tooltip.dataPoints[0]?.element;
            if (!anchor || !points.length) {
              element.style.display = "none";
              return;
            }
            element.replaceChildren(
              ...points.flatMap((point, index) =>
                tooltipLines(point).map((line, row) => {
                  const text = document.createElement("div");
                  text.textContent = line;
                  text.style.fontWeight = row === 0 ? "600" : "400";
                  text.style.color = row === 0 ? NYQUIST_TOOLTIP_COLORS.title : NYQUIST_TOOLTIP_COLORS.body;
                  if (index > 0 && row === 0) text.style.marginTop = "8px";
                  return text;
                }),
              ),
            );
            element.style.display = "block";
            const position = getNyquistTooltipPosition(
              anchor,
              { width: element.offsetWidth, height: element.offsetHeight },
              { width: container.clientWidth, height: container.clientHeight },
            );
            element.style.left = `${position.x}px`;
            element.style.top = `${position.y}px`;
          },
          displayColors: true,
          callbacks: {
            title: items => {
              const keys = new Set(
                items.map(item => data[item.datasetIndex]?.data[item.dataIndex]?.key).filter(Boolean),
              );
              if (keys.size > 1) return coincidentTitle;
              const point = items[0] ? data[items[0].datasetIndex]?.data[items[0].dataIndex] : null;
              return point ? formatSampleDate(point.sample.date) : "";
            },
            label: item => {
              const point = data[item.datasetIndex]?.data[item.dataIndex];
              return point ? tooltipLines(point) : [];
            },
          },
        },
      },
    };

    const chart = new Chart<"scatter", INyquistChartPoint[]>(canvas, {
      type: "scatter",
      data: { datasets: data },
      options,
      plugins: [decoration, overviewPlugin],
    });
    chartRef.current = chart;
    let resizeFrame = 0;
    const resize = () => {
      cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(() => {
        if (container.clientWidth > 0 && container.clientHeight > 0) {
          chart.resize(container.clientWidth, container.clientHeight);
          const area = chart.chartArea;
          if (area)
            setPlotSize(previous =>
              previous.width === area.width && previous.height === area.height
                ? previous
                : { width: area.width, height: area.height },
            );
        }
      });
    };
    const observer = new ResizeObserver(resize);
    observer.observe(container);
    resize();
    return () => {
      cancelAnimationFrame(resizeFrame);
      observer.disconnect();
      chartRef.current = null;
      tooltipHoveredRef.current = false;
      tooltipFocusedRef.current = false;
      if (tooltipRef.current) tooltipRef.current.style.display = "none";
      chart.destroy();
    };
  }, [
    coincidentTitle,
    applyZoom,
    clearZoomState,
    formatMessage,
    formatValue,
    kind,
    orderedSeries,
    overviewPlugin,
    series,
    sourceByKey,
    theme.palette,
    theme.typography.fontFamily,
    zoomState,
  ]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    chart.data.datasets.forEach((dataset, index) =>
      chart.setDatasetVisibility(index, !hiddenSeriesKeys?.includes(dataset.data[0]?.seriesKey)),
    );
    chart.update("none");
    setSelectionMenu(null);
  }, [hiddenSeriesKeys, orderedSeries]);

  useEffect(() => {
    chartRef.current?.draw();
  }, [cursorKey, rotationDirection, showRotationFrequency]);

  useEffect(() => {
    refreshOverview();
  }, [
    refreshOverview,
    rotationDirection,
    showRotationFrequency,
    formatValue,
    formatMessage,
    theme.palette,
    theme.typography.fontFamily,
  ]);

  return (
    <Box
      ref={containerRef}
      data-nyquist-chart={kind}
      data-nyquist-cursor-key={cursorKey ?? ""}
      onMouseOutCapture={event => {
        const next = event.relatedTarget;
        tooltipHoveredRef.current = next instanceof Node && Boolean(tooltipRef.current?.contains(next));
      }}
      sx={{
        flex: "1 1 0",
        height: 0,
        minHeight: 0,
        minWidth: 0,
        width: "100%",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <ChartZoomOverview overview={overview} zoomState={effectiveZoomState} onPan={panZoom} />
      {isZoomed ? (
        <MuiTooltip title={formatMessage({ id: "pages.scheme.analytics.resetZoom" })}>
          <IconButton
            size="small"
            aria-label={formatMessage({ id: "pages.scheme.analytics.resetZoom" })}
            onClick={resetZoom}
            sx={{ position: "absolute", top: 4, left: 4, zIndex: 2, bgcolor: "background.paper" }}
          >
            <ZoomOutMapIcon fontSize="small" />
          </IconButton>
        </MuiTooltip>
      ) : null}
      <canvas
        ref={canvasRef}
        role="img"
        tabIndex={0}
        aria-label={chartLabel}
        aria-describedby={`${hintId} ${frequencyDescriptionId}`}
        onKeyDown={event => {
          if (event.altKey || event.ctrlKey || event.metaKey) return;
          if (event.key === "Escape") {
            resetZoom();
            setSelectionMenu(null);
            return;
          }
          const next = getNyquistKeyboardCursor(keyboardSeries, cursorKey, event.key, kind);
          if (next) {
            event.preventDefault();
            onCursorChange(next);
          }
        }}
        onDoubleClick={resetZoom}
        onMouseDownCapture={event => {
          if (event.button === 0) {
            suppressNextClickRef.current = false;
            tooltipHoveredRef.current = false;
            tooltipFocusedRef.current = false;
            if (tooltipRef.current) tooltipRef.current.style.display = "none";
          }
          if (event.button === 1 && isZoomed) {
            event.preventDefault();
            resetZoom();
          }
        }}
        style={{ display: "block", width: "100%", height: "100%" }}
      >
        {chartLabel}
      </canvas>
      <Box
        ref={tooltipRef}
        role="tooltip"
        tabIndex={0}
        data-nyquist-tooltip={kind}
        onMouseEnter={() => {
          tooltipHoveredRef.current = true;
        }}
        onMouseLeave={() => {
          tooltipHoveredRef.current = false;
          if (!tooltipFocusedRef.current && tooltipRef.current) tooltipRef.current.style.display = "none";
        }}
        onFocus={() => {
          tooltipFocusedRef.current = true;
        }}
        onBlur={() => {
          tooltipFocusedRef.current = false;
          if (!tooltipHoveredRef.current && tooltipRef.current) tooltipRef.current.style.display = "none";
        }}
        onKeyDown={event => {
          if (event.key === "Escape") {
            event.stopPropagation();
            tooltipFocusedRef.current = false;
            tooltipHoveredRef.current = false;
            if (tooltipRef.current) tooltipRef.current.style.display = "none";
            canvasRef.current?.focus();
          }
        }}
        sx={{
          display: "none",
          position: "absolute",
          pointerEvents: "auto",
          zIndex: 3,
          maxWidth: "min(360px, calc(100% - 8px))",
          maxHeight: "calc(100% - 8px)",
          overflowY: "auto",
          overscrollBehavior: "contain",
          boxSizing: "border-box",
          p: 1,
          borderRadius: 1,
          bgcolor: NYQUIST_TOOLTIP_COLORS.background,
          color: NYQUIST_TOOLTIP_COLORS.body,
          fontSize: 12,
          fontFamily: theme.typography.fontFamily,
          lineHeight: 1.5,
          overflowWrap: "anywhere",
          boxShadow: 3,
        }}
      />
      <Box
        id={hintId}
        component="span"
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "1px",
          height: "1px",
          overflow: "hidden",
          clipPath: "inset(50%)",
        }}
      >
        {keyboardHint}
      </Box>
      <Box
        id={frequencyDescriptionId}
        component="span"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "1px",
          height: "1px",
          overflow: "hidden",
          clipPath: "inset(50%)",
        }}
      >
        {frequencyDescription}
      </Box>
      <Menu
        open={visibleMenu !== null}
        anchorReference="anchorPosition"
        anchorPosition={visibleMenu?.position}
        onClose={() => setSelectionMenu(null)}
        MenuListProps={{
          "aria-label": selectionLabel,
          subheader: <ListSubheader disableSticky>{coincidentTitle}</ListSubheader>,
        }}
      >
        {visibleMenu?.points.map(point => {
          const source = sourceByKey.get(point.seriesKey);
          if (!source) return null;
          return (
            <MenuItem
              key={point.key}
              data-nyquist-menu-sample-key={point.key}
              selected={point.key === cursorKey}
              onClick={() => {
                onCursorChangeRef.current(point.key);
                setSelectionMenu(null);
              }}
              sx={{ maxWidth: "min(440px, calc(100vw - 32px))", whiteSpace: "normal", alignItems: "flex-start" }}
            >
              <ListItemText
                primary={source.name}
                secondary={`${formatSampleDate(point.sample.date)} · ${formatPointValues(point, source, formatValue)}${
                  kind === "polar" && showRotationFrequency
                    ? ` · ${formatFrequency(point.sample.rotationFrequencyRpm)}`
                    : ""
                }`}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};
